'use client'

import {
  Check,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Kanban,
  List,
  MessageSquare,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react'
import * as React from 'react'

import { cn } from '../cn'
import { Badge, type BadgeProps } from './badge'
import { Button } from './button'
import { Checkbox } from './checkbox'
import { EmptyState } from './empty-state'
import { Input } from './input'
import { KanbanBoard, type KanbanColumn, KanbanColumnEmpty } from './kanban-board'
import { LIST_ROW_CLASS } from './list-row'
import { type OverflowTabItem, OverflowTabs } from './overflow-tabs'
import {
  TaskBoardCard,
  type TaskBoardCardCount,
  type TaskBoardCardPill,
  type TaskBoardCardTone,
} from './task-board-card'

export type TaskListTone = 'default' | 'muted' | 'danger' | 'warning' | 'success'
export type TaskListView = 'list' | 'board'
export type TaskListToolbarPlacement = 'top-row' | 'below-tabs-left'
export type TaskListCompletionDisplay = 'task' | 'live'

export interface TaskListItem {
  id: string
  title: string
  completed?: boolean
  completionDisplay?: TaskListCompletionDisplay
  completionDisabled?: boolean
  completionPending?: boolean
  tagLabel?: string | null
  tagTone?: TaskListTone
  dueLabel?: string | null
  dueTone?: TaskListTone
  projectLabel?: string | null
  statusLabel?: string | null
  statusTone?: TaskListTone
  assigneeLabel?: string | null
  assigneeImage?: string | null
  relatedLabel?: string | null
  /** Non-archived comment count. Renders a MessageSquare indicator when > 0. */
  commentCount?: number
  externalHref?: string | null
  externalLabel?: string
}

export interface TaskListSection {
  id: string
  title: string
  count?: number
  items: TaskListItem[]
  emptyTitle?: string
  emptyDescription?: string
  defaultOpen?: boolean
}

export interface TaskListScope {
  id: string
  label: string
  count?: number
}

export interface TaskListProps {
  sections: TaskListSection[]
  sectionStyle?: 'card' | 'collapsible'
  scopes?: TaskListScope[]
  activeScope?: string
  onScopeChange?: (scope: string) => void
  view?: TaskListView
  onViewChange?: (view: TaskListView) => void
  searchValue?: string
  searchPlaceholder?: string
  onSearchChange?: (value: string) => void
  filterControls?: React.ReactNode
  filterLabel?: string
  activeFilterCount?: number
  onFilterClick?: () => void
  onClearFilters?: () => void
  toolbarPlacement?: TaskListToolbarPlacement
  boardColumnClassName?: string
  onItemSelect?: (id: string) => void
  selectedItemIds?: ReadonlySet<string> | string[]
  onItemSelectionChange?: (id: string, selected: boolean) => void
  onItemComplete?: (id: string) => void
  fullBleedContent?: boolean
  className?: string
}

const emptySelectedItemIds = new Set<string>()

export function TaskList({
  sections,
  sectionStyle = 'card',
  scopes = [],
  activeScope,
  onScopeChange,
  view = 'list',
  onViewChange,
  searchValue,
  searchPlaceholder = 'Search tasks...',
  onSearchChange,
  filterControls,
  filterLabel = 'Filters',
  activeFilterCount = 0,
  onFilterClick,
  onClearFilters,
  toolbarPlacement = 'top-row',
  boardColumnClassName,
  onItemSelect,
  selectedItemIds,
  onItemSelectionChange,
  onItemComplete,
  fullBleedContent = false,
  className,
}: TaskListProps) {
  const hasSearch = typeof searchValue === 'string'
  const searchInFilterRow = hasSearch && toolbarPlacement === 'below-tabs-left'
  const searchInTopRow = hasSearch && !searchInFilterRow
  const showTopRow = Boolean(onViewChange) || searchInTopRow
  const showFilterRow = Boolean(onFilterClick) || Boolean(filterControls) || searchInFilterRow
  const showToolbar = showTopRow || scopes.length > 0 || showFilterRow
  const selectedItemIdSet = React.useMemo<ReadonlySet<string>>(
    () => (Array.isArray(selectedItemIds) ? new Set(selectedItemIds) : (selectedItemIds ?? emptySelectedItemIds)),
    [selectedItemIds],
  )

  return (
    <div className={cn('space-y-4', className)}>
      {showToolbar ? (
        <div className="space-y-3">
          {showTopRow ? (
            <div className="border-b border-border">
              <div className="flex items-center justify-between gap-3">
                {onViewChange ? <TaskListViewSwitcher view={view} onViewChange={onViewChange} /> : <span />}
                {searchInTopRow ? (
                  <TaskListSearch
                    variant="inline"
                    value={searchValue ?? ''}
                    placeholder={searchPlaceholder}
                    onChange={(next) => onSearchChange?.(next)}
                  />
                ) : null}
              </div>
            </div>
          ) : null}

          {scopes.length > 0 ? (
            <TaskListScopes scopes={scopes} activeScope={activeScope} onScopeChange={onScopeChange} />
          ) : null}

          {showFilterRow ? (
            <div className="flex flex-wrap items-center gap-2">
              {searchInFilterRow ? (
                <TaskListSearch
                  variant="toolbar"
                  value={searchValue ?? ''}
                  placeholder={searchPlaceholder}
                  onChange={(next) => onSearchChange?.(next)}
                />
              ) : null}

              {filterControls ? (
                <div className={cn('flex min-w-0 flex-wrap items-center gap-2', !searchInFilterRow && 'flex-1')}>
                  {filterControls}
                </div>
              ) : null}

              {onFilterClick ? (
                <Button
                  type="button"
                  variant={activeFilterCount > 0 ? 'secondary' : 'outline'}
                  size="sm"
                  onClick={onFilterClick}>
                  <SlidersHorizontal className="h-3.5 w-3.5" />
                  {filterLabel}
                  {activeFilterCount > 0 ? (
                    <Badge variant="default" className="h-4 min-w-4 px-1 text-[10px]">
                      {activeFilterCount}
                    </Badge>
                  ) : null}
                </Button>
              ) : null}

              {activeFilterCount > 0 && onClearFilters ? (
                <Button type="button" variant="ghost" size="sm" onClick={onClearFilters}>
                  <X className="h-3.5 w-3.5" />
                  Clear
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {view === 'board' ? (
        <TaskListBoard
          sections={sections}
          fullBleed={fullBleedContent}
          boardColumnClassName={boardColumnClassName}
          onItemSelect={onItemSelect}
          selectedItemIds={selectedItemIdSet}
          onItemSelectionChange={onItemSelectionChange}
          onItemComplete={onItemComplete}
        />
      ) : (
        <div
          className={cn(
            sectionStyle === 'collapsible' ? 'space-y-1' : 'space-y-4',
            (fullBleedContent || sectionStyle === 'collapsible') && '-mx-4 md:-mx-6',
          )}>
          {sections.map((section) => (
            <TaskListSectionView
              key={section.id}
              section={section}
              sectionStyle={sectionStyle}
              onItemSelect={onItemSelect}
              selectedItemIds={selectedItemIdSet}
              onItemSelectionChange={onItemSelectionChange}
              onItemComplete={onItemComplete}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function TaskListViewSwitcher({
  view,
  onViewChange,
}: {
  view: TaskListView
  onViewChange: (view: TaskListView) => void
}) {
  const views: OverflowTabItem[] = [
    {
      value: 'list',
      label: (
        <>
          <List className="h-4 w-4" />
          <span className="hidden sm:inline">List</span>
        </>
      ),
    },
    {
      value: 'board',
      label: (
        <>
          <Kanban className="h-4 w-4" />
          <span className="hidden sm:inline">Board</span>
        </>
      ),
    },
  ]

  return (
    <OverflowTabs
      items={views}
      value={view}
      onValueChange={(next) => onViewChange(next as TaskListView)}
      tone="transparent"
      divider="none"
      listClassName="gap-1"
      triggerClassName="gap-2 px-4 py-2 first:pl-0"
    />
  )
}

/**
 * Discrete, Notion-style search: a magnifier icon that expands into a
 * borderless inline input on click. Collapses back to the icon on blur
 * when empty; stays open while it holds a query. Sits top-right on the
 * view-switcher row.
 */
function TaskListSearch({
  variant,
  value,
  placeholder,
  onChange,
}: {
  variant: 'inline' | 'toolbar'
  value: string
  placeholder: string
  onChange: (value: string) => void
}) {
  const [open, setOpen] = React.useState(value.length > 0)

  if (variant === 'toolbar') {
    return (
      <div className="relative w-full max-w-full sm:w-[23rem]">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={value}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          className="h-9 pl-9 text-sm"
        />
      </div>
    )
  }

  const expanded = open || value.length > 0

  if (!expanded) {
    return (
      <button
        type="button"
        aria-label={placeholder}
        onClick={() => setOpen(true)}
        className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground">
        <Search className="h-4 w-4" />
      </button>
    )
  }

  return (
    <div className="relative w-44 md:w-56">
      <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <Input
        autoFocus
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        onBlur={() => {
          if (value.length === 0) {
            setOpen(false)
          }
        }}
        className="h-8 border-0 bg-transparent pl-7 pr-2 text-sm shadow-none focus-visible:ring-0"
      />
    </div>
  )
}

function TaskListScopes({
  scopes,
  activeScope,
  onScopeChange,
}: {
  scopes: TaskListScope[]
  activeScope?: string
  onScopeChange?: (scope: string) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {scopes.map((scope) => {
        const active = scope.id === activeScope
        return (
          <button
            key={scope.id}
            type="button"
            onClick={() => onScopeChange?.(scope.id)}
            className={cn(
              'inline-flex h-8 items-center gap-2 rounded-md border px-3 text-sm font-medium transition-colors',
              active
                ? 'border-input bg-muted text-foreground shadow-sm'
                : 'border-border bg-background text-muted-foreground hover:bg-muted/60 hover:text-foreground',
            )}>
            {scope.label}
            {typeof scope.count === 'number' ? (
              <span
                className={cn(
                  'rounded-full px-1.5 py-0.5 text-xs',
                  active ? 'bg-background text-foreground' : 'bg-muted text-muted-foreground',
                )}>
                {scope.count}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

function TaskListSectionView({
  section,
  sectionStyle,
  onItemSelect,
  selectedItemIds,
  onItemSelectionChange,
  onItemComplete,
}: {
  section: TaskListSection
  sectionStyle: 'card' | 'collapsible'
  onItemSelect?: (id: string) => void
  selectedItemIds: ReadonlySet<string>
  onItemSelectionChange?: (id: string, selected: boolean) => void
  onItemComplete?: (id: string) => void
}) {
  if (sectionStyle === 'collapsible') {
    return (
      <TaskListCollapsibleSection
        section={section}
        onItemSelect={onItemSelect}
        selectedItemIds={selectedItemIds}
        onItemSelectionChange={onItemSelectionChange}
        onItemComplete={onItemComplete}
      />
    )
  }

  return (
    <section className="overflow-hidden rounded-md border bg-card">
      <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
        <h2 className="font-semibold">{section.title}</h2>
        {typeof section.count === 'number' ? <Badge variant="secondary">{section.count}</Badge> : null}
      </div>

      {section.items.length === 0 ? (
        <EmptyState
          variant="plain"
          padding="sm"
          title={section.emptyTitle ?? 'No Tasks'}
          description={section.emptyDescription ?? 'Open tasks will appear here.'}
        />
      ) : (
        <ul>
          {section.items.map((item) => (
            <TaskListRow
              key={item.id}
              item={item}
              onItemSelect={onItemSelect}
              selectedItemIds={selectedItemIds}
              onItemSelectionChange={onItemSelectionChange}
              onItemComplete={onItemComplete}
            />
          ))}
        </ul>
      )}
    </section>
  )
}

function TaskListCollapsibleSection({
  section,
  onItemSelect,
  selectedItemIds,
  onItemSelectionChange,
  onItemComplete,
}: {
  section: TaskListSection
  onItemSelect?: (id: string) => void
  selectedItemIds: ReadonlySet<string>
  onItemSelectionChange?: (id: string, selected: boolean) => void
  onItemComplete?: (id: string) => void
}) {
  const hasItems = section.items.length > 0
  const [isOpen, setIsOpen] = React.useState(section.defaultOpen ?? hasItems)
  const expanded = hasItems && isOpen

  React.useEffect(() => {
    if (!hasItems) {
      setIsOpen(false)
    } else if (section.defaultOpen === undefined) {
      setIsOpen(true)
    }
  }, [hasItems, section.defaultOpen])

  return (
    <section className="mb-1">
      <button
        type="button"
        onClick={() => {
          if (hasItems) {
            setIsOpen((next) => !next)
          }
        }}
        className={cn(
          'group flex w-full items-center gap-2 px-4 py-2 text-left transition-colors',
          hasItems ? 'hover:bg-muted/50' : 'cursor-default',
        )}>
        {expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <span className="min-w-0 truncate text-base font-semibold md:text-sm">{section.title}</span>
        <span className="text-xs text-muted-foreground">{section.count ?? section.items.length}</span>
        <span className="flex-1" />
      </button>
      {expanded ? (
        <ul>
          {section.items.map((item) => (
            <TaskListRow
              key={item.id}
              item={item}
              onItemSelect={onItemSelect}
              selectedItemIds={selectedItemIds}
              onItemSelectionChange={onItemSelectionChange}
              onItemComplete={onItemComplete}
            />
          ))}
        </ul>
      ) : null}
    </section>
  )
}

function TaskListRow({
  item,
  onItemSelect,
  selectedItemIds,
  onItemSelectionChange,
  onItemComplete,
}: {
  item: TaskListItem
  onItemSelect?: (id: string) => void
  selectedItemIds: ReadonlySet<string>
  onItemSelectionChange?: (id: string, selected: boolean) => void
  onItemComplete?: (id: string) => void
}) {
  const canComplete = Boolean(onItemComplete) && !item.completionDisabled
  const selectable = Boolean(onItemSelectionChange)
  const selected = selectedItemIds.has(item.id)
  const showCompletionControl = Boolean(onItemComplete) || !selectable
  const interactive = Boolean(onItemSelect)
  const completedAsTask = item.completed && (item.completionDisplay ?? 'task') === 'task'
  const titleContent = (
    <span className={cn('block truncate', completedAsTask && 'line-through text-muted-foreground')}>{item.title}</span>
  )

  return (
    <li className="border-b border-border">
      <div
        onClick={() => onItemSelect?.(item.id)}
        className={cn(LIST_ROW_CLASS, 'group hover:bg-muted/50 transition-colors', interactive && 'cursor-pointer')}>
        {selectable ? (
          <Checkbox
            checked={selected}
            aria-label={`Select ${item.title}`}
            onCheckedChange={(checked) => onItemSelectionChange?.(item.id, checked === true)}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          />
        ) : null}

        {showCompletionControl ? (
          <button
            type="button"
            aria-label={`Complete ${item.title}`}
            disabled={!canComplete}
            onClick={(event) => {
              event.stopPropagation()
              onItemComplete?.(item.id)
            }}
            onKeyDown={(event) => event.stopPropagation()}
            className={cn(
              'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
              item.completed ? 'border-transparent bg-green-700/60 text-white' : 'border-muted-foreground/20',
              canComplete && !item.completed && 'hover:border-green-600/60',
              !canComplete && 'cursor-default',
            )}>
            {(item.completed || item.completionPending) && <Check className="h-3 w-3" />}
          </button>
        ) : null}

        <div className="min-w-0 flex-1 text-left text-sm">{titleContent}</div>

        {item.tagLabel ? (
          <Badge
            variant={tagBadgeVariant(item.tagTone)}
            className="max-w-[150px] shrink-0 truncate rounded-md px-2 py-0.5">
            {item.tagLabel}
          </Badge>
        ) : null}

        {item.projectLabel ? (
          <span className="hidden max-w-[180px] shrink-0 truncate rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground md:inline-flex">
            {item.projectLabel}
          </span>
        ) : null}

        {item.statusLabel ? (
          <span className={cn('hidden shrink-0 text-xs md:inline-flex', toneClassName(item.statusTone))}>
            {item.statusLabel}
          </span>
        ) : null}

        {item.relatedLabel ? (
          <span className="hidden max-w-[160px] shrink-0 truncate text-xs text-muted-foreground xl:inline">
            {item.relatedLabel}
          </span>
        ) : null}

        {item.commentCount && item.commentCount > 0 ? (
          <span className="flex shrink-0 items-center gap-0.5 text-xs text-muted-foreground">
            <MessageSquare className="h-3 w-3" />
            {item.commentCount}
          </span>
        ) : null}

        {item.dueLabel ? (
          <span className={cn('shrink-0 whitespace-nowrap text-xs', toneClassName(item.dueTone))}>{item.dueLabel}</span>
        ) : null}

        {item.externalHref ? (
          <a
            href={item.externalHref}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            className="hidden shrink-0 items-center gap-1 text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline md:inline-flex">
            <ExternalLink className="h-3 w-3" />
            {item.externalLabel ?? 'Open'}
          </a>
        ) : null}

        {item.assigneeLabel ? <TaskAssigneeAvatar label={item.assigneeLabel} image={item.assigneeImage} /> : null}
      </div>
    </li>
  )
}

function TaskListBoard({
  sections,
  fullBleed,
  boardColumnClassName,
  onItemSelect,
  selectedItemIds,
  onItemSelectionChange,
  onItemComplete,
}: {
  sections: TaskListSection[]
  fullBleed: boolean
  boardColumnClassName?: string
  onItemSelect?: (id: string) => void
  selectedItemIds: ReadonlySet<string>
  onItemSelectionChange?: (id: string, selected: boolean) => void
  onItemComplete?: (id: string) => void
}) {
  const columns = React.useMemo<KanbanColumn<TaskListItem>[]>(
    () =>
      sections.map((section) => ({
        id: section.id,
        title: section.title,
        count: section.count ?? section.items.length,
        items: section.items,
        empty: (
          <KanbanColumnEmpty>
            <span>{section.emptyTitle ?? 'No Tasks'}</span>
          </KanbanColumnEmpty>
        ),
      })),
    [sections],
  )

  return (
    <div className={cn('min-h-[520px] overflow-hidden', fullBleed && '-mx-4 md:-mx-6')}>
      <KanbanBoard
        columns={columns}
        disableDrag
        desktopColumnClassName={boardColumnClassName}
        className={cn(!fullBleed && 'px-0', 'pb-3')}
        renderCard={(item, meta) => (
          <TaskListBoardCard
            item={item}
            isDragging={meta.isDragging}
            onItemSelect={onItemSelect}
            selected={selectedItemIds.has(item.id)}
            onItemSelectionChange={onItemSelectionChange}
            onItemComplete={onItemComplete}
          />
        )}
      />
    </div>
  )
}

function TaskListBoardCard({
  item,
  isDragging,
  onItemSelect,
  selected,
  onItemSelectionChange,
  onItemComplete,
}: {
  item: TaskListItem
  isDragging: boolean
  onItemSelect?: (id: string) => void
  selected: boolean
  onItemSelectionChange?: (id: string, selected: boolean) => void
  onItemComplete?: (id: string) => void
}) {
  const canComplete = Boolean(onItemComplete) && !item.completionDisabled
  const selectable = Boolean(onItemSelectionChange)

  return (
    <div className="relative">
      <TaskBoardCard
        title={item.title}
        completed={item.completed}
        completionDisplay={item.completionDisplay}
        completionDisabled={!canComplete}
        completionPending={item.completionPending}
        selected={selected}
        multiSelected={selectable && selected}
        isDragging={isDragging}
        assigneeLabel={item.assigneeLabel}
        assigneeImage={item.assigneeImage}
        dueLabel={item.dueLabel}
        dueTone={item.dueTone}
        pills={taskBoardCardPills(item)}
        counts={taskBoardCardCounts(item)}
        className={selectable ? 'pr-9' : undefined}
        onSelect={onItemSelect ? () => onItemSelect(item.id) : undefined}
        onComplete={canComplete ? () => onItemComplete?.(item.id) : undefined}
      />
      {selectable ? (
        <Checkbox
          checked={selected}
          aria-label={`Select ${item.title}`}
          className="absolute right-2 top-2 bg-card"
          onCheckedChange={(checked) => onItemSelectionChange?.(item.id, checked === true)}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        />
      ) : null}
    </div>
  )
}

function taskBoardCardPills(item: TaskListItem): TaskBoardCardPill[] {
  const pills: TaskBoardCardPill[] = []
  if (item.tagLabel) {
    pills.push({ id: 'tag', label: item.tagLabel, tone: taskBoardCardTone(item.tagTone) })
  }
  if (item.projectLabel) {
    pills.push({ id: 'project', label: item.projectLabel })
  }
  if (item.statusLabel) {
    pills.push({ id: 'status', label: item.statusLabel, tone: taskBoardCardTone(item.statusTone) })
  }
  if (item.relatedLabel) {
    pills.push({ id: 'related', label: item.relatedLabel })
  }
  return pills
}

function taskBoardCardCounts(item: TaskListItem): TaskBoardCardCount[] {
  const counts: TaskBoardCardCount[] = []
  if (item.commentCount && item.commentCount > 0) {
    counts.push({ id: 'comments', icon: <MessageSquare className="h-3 w-3" />, count: item.commentCount })
  }
  return counts
}

function taskBoardCardTone(tone: TaskListTone | undefined): TaskBoardCardTone {
  return tone ?? 'muted'
}

function tagBadgeVariant(tone: TaskListTone | undefined): BadgeProps['variant'] {
  switch (tone) {
    case 'danger':
      return 'destructive'
    case 'warning':
      return 'warning'
    case 'success':
      return 'success'
    case 'default':
      return 'default'
    case 'muted':
    default:
      return 'secondary'
  }
}

function TaskAssigneeAvatar({ label, image }: { label: string; image?: string | null }) {
  const initials = assigneeInitials(label)

  if (image) {
    return (
      <img
        src={image}
        alt={label}
        title={label}
        className="h-7 w-7 shrink-0 rounded-full border border-border object-cover"
      />
    )
  }

  return (
    <span
      title={label}
      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border bg-muted text-[11px] font-semibold text-muted-foreground">
      {initials}
    </span>
  )
}

function assigneeInitials(label: string): string {
  const words = label.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) {
    return '?'
  }
  if (words.length === 1) {
    return words[0]!.slice(0, 2).toUpperCase()
  }
  return `${words[0]![0]}${words[words.length - 1]![0]}`.toUpperCase()
}

function toneClassName(tone: TaskListTone = 'muted'): string {
  switch (tone) {
    case 'danger':
      return 'text-destructive'
    case 'warning':
      return 'text-amber-700 dark:text-amber-400'
    case 'success':
      return 'text-emerald-700 dark:text-emerald-400'
    case 'default':
      return 'text-foreground'
    case 'muted':
    default:
      return 'text-muted-foreground'
  }
}
